import type {
	StripeCardCvcElementChangeEvent,
	StripeCardExpiryElementChangeEvent,
	StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';

import type { WithTokenParams } from 'src/app/shared/types';
import type { PaymentMethodSaveOptions, PaymentType } from 'src/app/shared/utils/payments';

import type { NewPaymentMethodStripeProps } from '../../index.tsx';

import { useTracking } from './useTracking';

type Result = ModelHookResult<
	{
		email: string;
		name: string;
		paymentMethodSaveOptions: PaymentMethodSaveOptions;
	},
	{
		beforeToken: (paymentType: PaymentType) => Promise<boolean>;
		onTokenError: (paymentType: PaymentType, err: Error) => void;
		onTokenInvalid: (paymentType: PaymentType, err: Error) => void;
		onCardDetailsFieldFocus: () => void;
		onChange?: (
			e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
		) => void;
		validate: (paymentType: PaymentType) => Promise<boolean>;
		withToken: (params: WithTokenParams) => Promise<void>;
		prePaymentChecks: (paymentType: PaymentType) => Promise<boolean>;
	}
>;

export function useNewPaymentMethodStripe({
	email,
	name,
	paymentMethodSaveOptions,
	beforeToken,
	onTokenError,
	onTokenInvalid,
	onChange,
	withToken,
	validate,
	prePaymentChecks,
}: Omit<NewPaymentMethodStripeProps, 'submitButton' | 'phoneNumberInput'>): Result {
	const { trackCardFieldFocus } = useTracking();

	return {
		data: {
			email,
			name,
			paymentMethodSaveOptions,
		},
		actions: {
			beforeToken,
			onTokenError,
			onTokenInvalid,
			onCardDetailsFieldFocus: () => trackCardFieldFocus('card'),
			onChange,
			validate,
			withToken,
			prePaymentChecks,
		},
	};
}
